import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useImpersonatedAuth } from '../hooks/useImpersonatedAuth';
import { api } from '../services/api';

interface Company {
  id: string;
  name: string;
  domain: string;
}

interface CompanyContextType {
  selectedCompanyId: string | null;
  availableCompanies: Company[];
  hasMultipleCompanies: boolean;
  setSelectedCompany: (companyId: string | null) => void;
  getCurrentCompany: () => Company | null;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompanyContext = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompanyContext must be used within a CompanyProvider');
  }
  return context;
};

export const CompanyProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useImpersonatedAuth();
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);

  // State to hold all companies for system admins
  const [allCompanies, setAllCompanies] = useState<Company[]>([]);

  // Fetch all companies if user is system admin
  useEffect(() => {
    if (user?.is_system_admin) {
      const fetchAllCompanies = async () => {
        try {
          const response = await api.get('/companies');
          setAllCompanies(response.data.data.map((c: any) => ({
            id: c.id,
            name: c.name,
            domain: c.domain
          })));
        } catch (error) {
          console.error('Failed to fetch all companies:', error);
        }
      };
      fetchAllCompanies();
    }
  }, [user?.is_system_admin]);

  // Combined available companies
  const availableCompanies = useMemo(() => {
    const userCompaniesRaw = user?.userCompanies || user?.companies || [];
    let companies: Company[] = [];

    if (user?.is_system_admin && allCompanies.length > 0) {
      companies = allCompanies;
    } else {
      companies = userCompaniesRaw.map((uc: any) => {
        const companyObj = uc.company || uc.companyDetails;
        return {
          id: uc.company_id,
          name: companyObj?.name || 'Unknown Company',
          domain: companyObj?.domain
        };
      });
    }

    return companies;
    // We only want to recalculate if the actual company data changes, not just the user object reference
  }, [user?.is_system_admin, allCompanies, user?.userCompanies, user?.companies]);

  const hasMultipleCompanies = availableCompanies.length > 1;

  // Get current company
  const getCurrentCompany = useCallback((): Company | null => {
    if (!selectedCompanyId) return null;
    return availableCompanies.find((company: Company) => company.id === selectedCompanyId) || null;
  }, [selectedCompanyId, availableCompanies]);

  // Set selected company
  const setSelectedCompany = useCallback((companyId: string | null) => {
    setSelectedCompanyId(companyId);
    // Store in localStorage for persistence
    if (companyId) {
      localStorage.setItem('selectedCompanyId', companyId);
    } else {
      localStorage.removeItem('selectedCompanyId');
    }
  }, []);

  // Initialize selected company on user load
  useEffect(() => {
    if (availableCompanies.length > 0) {
      // Avoid resetting if already set to a valid company
      const currentValid = selectedCompanyId && availableCompanies.some(c => c.id === selectedCompanyId);
      if (currentValid) return;

      // For multiple companies, default to "All Companies" (null)
      // For single company, use that company as default
      if (availableCompanies.length > 1) {
        setSelectedCompanyId(null);
      } else {
        setSelectedCompanyId(availableCompanies[0].id);
      }
    } else {
      setSelectedCompanyId(null);
    }
  }, [availableCompanies, selectedCompanyId]);

  const value = useMemo(() => ({
    selectedCompanyId,
    availableCompanies,
    hasMultipleCompanies,
    setSelectedCompany,
    getCurrentCompany
  }), [selectedCompanyId, availableCompanies, hasMultipleCompanies, setSelectedCompany, getCurrentCompany]);

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
};

